import { CustomItemLibrary, slotFilter } from '@/components/item/ItemList'
import { InventorySlotType } from '@/data/enums'
import { CallbackWrap } from '@/types/common'
import { Embed } from '@/types/embed'
import { LevelingEmbed, LevelingMilestone, LevelingMilestoneGroup } from '@/types/leveling'
import { ItemId } from '@/types/profile'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import { Input } from 'components/editor/Input'
import { RichTextEditor } from 'components/editor/rich-text-editor/RichTextEditor'
import Paperdoll from 'components/embeds/Paperdoll'
import React from 'react'
import './progression-milestone.scss'

function changeMilestone(
  id: string,
  milestones: Exclude<LevelingEmbed['milestones'], undefined>,
  callback: (milestone: LevelingMilestone) => void
) {
  return milestones.map((group) => {
    if (group.id === id) {
      group = { ...group }
      callback(group)
      return group
    } else if ('items' in group) {
      const index = group.items.findIndex((m) => m.id === id)
      if (index >= 0) {
        const item = { ...group.items[index] }
        callback(item)
        const result = { ...group }
        result.items = result.items.slice()
        result.items[index] = item
        return result
      }
      return group
    } else {
      return group
    }
  })
}

export type NumberedMilestone = LevelingMilestone & { index: number }
export type NumberedMilestoneGroup = Omit<LevelingMilestoneGroup, 'items'> & {
  items: NumberedMilestone[]
}

export function ProgressionMilestone({
  milestone,
  completed,
  toggleCompleted,
  updateSignal,
  id,
  embed,
  onEmbedChange,
  isActiveTab
}: {
  milestone: NumberedMilestone
  completed: boolean
  toggleCompleted: (id: string) => void
  updateSignal: number
  id: string
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
  isActiveTab?: boolean
}) {
  const [isEditing, setIsEditing] = React.useState(false)
  const [isHighlighted, setIsHighlighted] = React.useState(false)

  const firstRender = React.useRef(true)
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }

    setIsHighlighted(true)
    const timer = setTimeout(() => {
      setIsHighlighted(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [updateSignal])

  const updateMilestone = React.useCallback(
    (callback: (value: LevelingMilestone) => void) => {
      const milestoneId = milestone.id
      onEmbedChange?.((embed) => {
        if (embed.type !== 'leveling') return embed
        embed = { ...embed }
        if (!embed.milestones) return embed
        embed.milestones = changeMilestone(milestoneId, embed.milestones, callback)
        return embed
      })
    },
    [milestone.id, onEmbedChange]
  )

  // Equipment
  const [slot, setSlot] = React.useState<InventorySlotType>()
  const onEquip = React.useCallback(
    (id: ItemId) =>
      slot &&
      updateMilestone((newMilestone) => {
        newMilestone.equipmentItems = {
          ...newMilestone.equipmentItems,
          [slot]: id
        }
      }),
    [updateMilestone, slot]
  )

  const filter = React.useMemo(() => (slot ? slotFilter(slot) : undefined), [slot])

  return (
    <div
      className={classNames(
        'poe-progression-card',
        isHighlighted && 'poe-highlight',
        completed && 'poe-completed',
        milestone.visible === false && 'poe-card-hidden'
      )}
      id={id}>
      <div className="poe-card-title-box">
        <Checkbox
          type="sm"
          value={completed}
          onChange={() => {
            toggleCompleted(milestone.id)
          }}
        />
        <div className="poe-card-title">
          {isEditing ? (
            <Input
              value={milestone.title}
              onChange={(newValue) => {
                updateMilestone((newMilestone) => {
                  newMilestone.title = newValue
                })
              }}
            />
          ) : (
            `${milestone.index}. ${milestone.title}`
          )}
        </div>
        <span className="poe-card-title-right">
          {!!onEmbedChange &&
            (isEditing ? (
              <button onClick={() => setIsEditing(false)}>Close</button>
            ) : (
              <FontAwesomeIcon
                icon={faEdit}
                onClick={() => {
                  setIsEditing(!isEditing)
                }}
              />
            ))}
        </span>
      </div>

      <div className={classNames('poe-card-content poe-editable-markdown')}>
        <div className="poe-editable-markdown-content">
          <RichTextEditor
            render={true}
            key={`${embed.id}-milestone-${milestone.id}-content`}
            value={milestone.content}
            isEditing={isEditing}
            onChange={
              onEmbedChange
                ? (newValue) => {
                    updateMilestone((newMilestone) => {
                      newMilestone.content = newValue
                    })
                  }
                : undefined
            }
          />

          {isEditing && (
            <div className="poe-editable-paperdoll">
              <Paperdoll items={milestone.equipmentItems || {}} selected={slot} onSelect={setSlot} />

              {!!slot && (
                <CustomItemLibrary
                  filter={filter}
                  selected={milestone.equipmentItems?.[slot]}
                  onSelect={onEquip}
                  showEmpty
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
